import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	Row,
	Section,
	stringToLocalDate,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { SlimEmptyView } from "../../../../../../Components/Empty";
import { Icon } from "../../../../../../Components/Icon";
import { Tile } from "../../../../../../Components/Tile";
import { Timeline } from "../../../../../../Components/Timeline";
import {
	OrderDirection,
	Student,
	StudentDataConsentOrderField,
	StudentDetailQuery,
	StudentMedicalConditionOrderField,
	StudentNameFragment,
	StudentNoteOrderField,
	TimelineFilterInput,
	TimelineObjectType,
	useStudentAcademicOverviewQuery,
	useStudentDataConsentDetailListQuery,
	useStudentMedicalConditionCountQuery,
	useStudentMedicalConditionStudentListQuery,
	useStudentNoteStudentPinnedListQuery,
} from "../../../../../../Utilities";
import { GuardianFormat, PersonFormat, RelationshipFormat, StudentFormat } from "../../../../../Format";
import { StudentCombinedForm } from "../../../../../Forms";
import { CampusLink, ClassYearLink, EmployeeLink, HomeRoomSectionLink } from "../../../../../Links";
import { AcademicTermUtilities, StudentDataConsentUtilities } from "../../../../../Utilities";
import * as GuardianComponents from "../../../../Guardian";
import { StudentDataConsentModal } from "../../../../StudentDataConsent";
import {
	StudentMedicalConditionStudentListModal,
	StudentMedicalConditionStudentListModalProps,
	StudentMedicalConditionViewPermissionRequired,
} from "../../../../StudentMedicalCondition";
import { PinnedStudentNotesRow } from "../../../../StudentNote";
import { DetailRouteContext } from "../../RouteContext";
import styles from "./styles.module.css";

export type OverviewBodyProps = {
	student: StudentDetailQuery["student"];
};

export function OverviewBody({ student }: OverviewBodyProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);
	useActivePageRegistration(subPageInfos.overview);

	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column justify="spaced-start" className={styles["overview-body__side-bar"]} style={{ flexShrink: 0 }}>
				<DetailsSection student={student} />
				<ContactsSection student={student} />
			</Column>

			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<PinnedNotes student={student} style={{ width: "100%" }} />

				<TimelineTile student={student} />
			</Column>
		</Row>
	);
}

type DetailsSectionProps = {
	student: StudentDetailQuery["student"];
};

function DetailsSection({ student }: DetailsSectionProps) {
	const { currentAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();

	const { loading, data } = useStudentAcademicOverviewQuery({
		variables: {
			studentID: student.id,
			academicTermID: currentAcademicTerm?.id ?? "0",
			yearsFromGraduation: student.yearsFromGraduation,
		},
		skip: !currentAcademicTerm,
	});
	const campus = data?.campusConnection.edges[0]?.node;
	const classYear = data?.classYearConnection.edges[0]?.node;
	const homeRoomSection = data?.homeRoomSectionConnection.edges[0]?.node;
	const teacher = homeRoomSection?.teacherHomeRoomSectionEnrollments[0]?.teacher;

	return (
		<Section className={styles["overview-body__details"]}>
			<Section.Header>
				<Heading.H2 noMargin>Student Details</Heading.H2>

				<div className={styles["overview-body__details__edit-button__wrapper"]}>
					<DetailFormController student={student} />
				</div>
			</Section.Header>
			<Section.Body>
				<Column>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Name</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{StudentFormat.name(student)}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Campus</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{campus && <CampusLink instance={campus} className="link--no-text-decoration" />}
						</TruncatedText>
					</Row>
					<If condition={student.graduationYear !== null}>
						<Row justify="space-between" className={styles["overview-body__details__detail"]}>
							<span className={styles["overview-body__details__detail__label"]}>Class Year</span>
							<TruncatedText className={styles["overview-body__details__detail__value"]}>
								{classYear && <ClassYearLink instance={classYear} className="link--no-text-decoration" />}
							</TruncatedText>
						</Row>
					</If>
					<If condition={!loading && !!homeRoomSection}>
						<Row justify="space-between" className={styles["overview-body__details__detail"]}>
							<span className={styles["overview-body__details__detail__label"]}>Home Room</span>
							<TruncatedText className={styles["overview-body__details__detail__value"]}>
								{homeRoomSection && (
									<HomeRoomSectionLink instance={homeRoomSection} className="link--no-text-decoration" />
								)}
							</TruncatedText>
						</Row>
						<Row justify="space-between" className={styles["overview-body__details__detail"]}>
							<span className={styles["overview-body__details__detail__label"]}>Teacher</span>
							<TruncatedText className={styles["overview-body__details__detail__value"]}>
								{teacher && <EmployeeLink instance={teacher} className="link--no-text-decoration" />}
							</TruncatedText>
						</Row>
					</If>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Age</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{PersonFormat.age(student.person.dateOfBirth)} (
							{student.person.dateOfBirth
								? stringToLocalDate(student.person.dateOfBirth, "day").toLocaleString(undefined, {
										year: "2-digit",
										month: "numeric",
										day: "numeric",
								  })
								: "Unknown"}
							)
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Gender</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{PersonFormat.Fields.gender(student.person.gender)}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Nationality</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{PersonFormat.Edges.nationalities(student.person.nationalities)}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Primary Language</span>
						<TruncatedText className={styles["overview-body__details__detail__value"]}>
							{PersonFormat.Edges.languages(student.person.languages)}
						</TruncatedText>
					</Row>
					<StudentMedicalConditionViewPermissionRequired>
						<MedicalConditionsDetailItem student={student} />
					</StudentMedicalConditionViewPermissionRequired>
					<DataConsentDetailItem student={student} />
				</Column>
			</Section.Body>
		</Section>
	);
}

type MedicalConditionsDetailItemProps = {
	student: StudentDetailQuery["student"];
};

function MedicalConditionsDetailItem({ student }: MedicalConditionsDetailItemProps) {
	const { loading, data } = useStudentMedicalConditionCountQuery({
		variables: {
			filters: { studentIDEQ: student.id },
		},
	});
	const numConditions = data?.studentMedicalConditionConnection.totalCount ?? 0;

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={
					styles["overview-body__details__detail"] +
					" " +
					styles["overview-body__details__detail--medical-conditions"] +
					(numConditions === 0 ? " " + styles["overview-body__details__detail--medical-conditions--none"] : "")
				}
			>
				<Row justify="space-between">
					<span className={styles["overview-body__details__detail__label"]}>Medical Conditions</span>
					<TruncatedText className={styles["overview-body__details__detail__value"]}>
						<Conditional>
							<If condition={loading && !data}>Checking...</If>
							<If condition={numConditions === 0}>None listed</If>
							<Else>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Asterisk color="var(--theme-colors__pink)" /> {numConditions}
								</Row>
							</Else>
						</Conditional>
					</TruncatedText>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<MedicalConditionsModal student={student} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type MedicalConditionsModalProps = Pick<StudentMedicalConditionStudentListModalProps, "student" | "ifRef" | "onClose">;

function MedicalConditionsModal({ student, ifRef, onClose }: MedicalConditionsModalProps) {
	const { loading, data } = useStudentMedicalConditionStudentListQuery({
		variables: {
			filters: { studentIDEQ: student.id },
			order: { field: StudentMedicalConditionOrderField.IsProminent, direction: OrderDirection.Desc },
		},
	});
	const studentMedicalConditions = React.useMemo(
		() => data?.studentMedicalConditionConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<StudentMedicalConditionStudentListModal
			ifRef={ifRef}
			isLoading={loading}
			student={student}
			studentMedicalConditions={studentMedicalConditions}
			onClose={onClose}
		/>
	);
}

type DataConsentDetailItemProps = {
	student: StudentDetailQuery["student"];
};

function DataConsentDetailItem({ student }: DataConsentDetailItemProps) {
	const { loading, data } = useStudentDataConsentDetailListQuery({
		variables: {
			filters: { studentIDEQ: student.id },
			order: { field: StudentDataConsentOrderField.CreatedAt, direction: OrderDirection.Desc },
			pagination: { first: 1 },
		},
	});
	const studentDataConsent = data?.studentDataConsentConnection.edges[0]?.node;
	const { numDenied } = StudentDataConsentUtilities.summarize(studentDataConsent);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={
					styles["overview-body__details__detail"] + " " + styles["overview-body__details__detail--data-consent"]
				}
			>
				<Row justify="space-between">
					<span className={styles["overview-body__details__detail__label"]}>Data Consent</span>
					<TruncatedText className={styles["overview-body__details__detail__value"]}>
						<Conditional>
							<If condition={loading && !data}>Checking...</If>
							<If condition={!studentDataConsent}>Not Submitted</If>
							<If condition={numDenied === 0}>
								<span style={{ color: "var(--theme-colors__green)" }}>All Allowed</span>
							</If>
							<Else>
								<span style={{ color: "var(--theme-colors__pink)" }}>Denied ({numDenied})</span>
							</Else>
						</Conditional>
					</TruncatedText>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentDataConsentModal
					student={student}
					isLoading={loading}
					studentDataConsent={studentDataConsent}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

const formStateArgs = { editMode: EditMode.WriteOnly };

type DetailFormControllerProps = {
	student: StudentDetailQuery["student"];
};

function DetailFormController({ student }: DetailFormControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="link" onClick={openModal} className="button--icon">
				<Icon.Gear size={16} className="block" />
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ height: "40rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Edit {PersonFormat.Fields.name(student.person.name)}</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<StudentCombinedForm.ControlledDetail
							student={student}
							studentFormStateArgs={formStateArgs}
							studentOnSuccess={closeModal}
							personFormStateArgs={formStateArgs}
							personOnSuccess={closeModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type ContactsSectionProps = {
	student: StudentDetailQuery["student"];
};

function ContactsSection({ student }: ContactsSectionProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);

	return (
		<Section className={styles["overview-body__contacts"]}>
			<Section.Header>
				<Row justify="space-between" align="center">
					<Heading.H2 noMargin>Primary Contacts</Heading.H2>
					<InternalLink
						to={subPageInfos.familyAndContact.to(student.id)}
						className={styles["overview-body__contacts__view-all-link"]}
					>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
							View All <Icon.ArrowRight />
						</Row>
					</InternalLink>
				</Row>
			</Section.Header>
			<Section.Body>
				<Conditional>
					<If condition={student.relationships.length === 0}>
						<SlimEmptyView>No contacts</SlimEmptyView>
					</If>
					<Else>
						<Row justify="spaced-start">
							{student.relationships.slice(0, 2).map((relationship) => (
								<ContactView key={relationship.id} relationship={relationship} student={student} />
							))}
						</Row>
					</Else>
				</Conditional>
			</Section.Body>
		</Section>
	);
}

type ContactViewProps = {
	relationship: StudentDetailQuery["student"]["relationships"][0];
	student: Pick<Student, "id"> & StudentNameFragment;
};

function ContactView({ relationship, student }: ContactViewProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className={styles["overview-body__contacts__contact"]}>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ overflow: "hidden" }}>
					<span className={styles["overview-body__contacts__contact__label"]}>
						{RelationshipFormat.Fields.relationshipType(relationship.relationshipType)}
					</span>
					<TruncatedText className={styles["overview-body__contacts__contact__name"]}>
						{GuardianFormat.name(relationship.guardian)}
					</TruncatedText>
					<Row
						justify="spaced-start"
						align="center"
						horizontalSpacing="0.625rem"
						className={styles["overview-body__contacts__contact__contact-methods"]}
					>
						<If condition={relationship.guardian.person.phoneNumbers.length > 0}>
							<Icon.Phone color="var(--primary-color)" />
						</If>
						<If condition={relationship.guardian.person.emailAddresses.length > 0}>
							<Icon.EnvelopeSimple color="var(--primary-color)" />
						</If>
					</Row>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<GuardianComponents.GuardianRelationshipModal
					relationship={relationship}
					student={student}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type PinnedNotesProps = {
	student: StudentDetailQuery["student"];
} & Partial<ClassNameProps & StyleProps>;

function PinnedNotes({ student, className, style }: PinnedNotesProps) {
	const { loading, data } = useStudentNoteStudentPinnedListQuery({
		variables: {
			filters: {
				primaryStudents: [{ idEQ: student.id }],
				isPinnedEQ: true,
			},
			order: { field: StudentNoteOrderField.CreatedAt, direction: OrderDirection.Desc },
		},
	});
	const studentNotes = React.useMemo(() => data?.studentNoteConnection.edges.map((e) => e.node), [data]);

	const studentNoteFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				primaryStudentIDs: [student.id],
			},
		}),
		[student.id],
	);

	return (
		<PinnedStudentNotesRow
			isLoading={loading}
			studentNotes={studentNotes}
			formStateArgs={studentNoteFormStateArgs}
			formNameSuffix={student.id}
			className={className}
			style={style}
		/>
	);
}

type TimelineTileProps = {
	student: StudentDetailQuery["student"];
};

function TimelineTile({ student }: TimelineTileProps) {
	const filter = React.useMemo<TimelineFilterInput>(
		() => ({
			objectTypeIn: [
				TimelineObjectType.AcademicTerm,
				TimelineObjectType.Announcement,
				TimelineObjectType.CalendarEvent,
				TimelineObjectType.Contract,
				TimelineObjectType.Post,
				TimelineObjectType.Payment,
				TimelineObjectType.SchoolAttendanceRecord,
				TimelineObjectType.StudentMedicalEvent,
				TimelineObjectType.StudentNote,
			],
			studentID: student.id,
		}),
		[student.id],
	);

	return (
		<Tile style={{ flexGrow: 1 }}>
			<Tile.Body>
				<Timeline filter={filter} pageSize={10} style={{ height: "100%" }} />
			</Tile.Body>
		</Tile>
	);
}
